import React, { useState, useEffect } from "react";
import { Link, StaticQuery, graphql } from "gatsby";
import { useLocation } from "@reach/router";
import { Container } from "react-bootstrap";

const normalizePath = (path, allowRoot = true) => {
    if (!path) return allowRoot ? "/" : "";

    const pathWithoutDomain = path.startsWith("http")
        ? `/${path.split("/").slice(3).join("/")}`
        : path;
    const pathWithoutParams = pathWithoutDomain.split("?")[0].split("#")[0];
    let pathname = pathWithoutParams || "/";

    if (!allowRoot && pathname === "/") {
        return "";
    }

    if (!pathname.startsWith("/")) {
        pathname = `/${pathname}`;
    }

    return pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
};

const isPathActive = (currentPath, itemPath) => {
    const normalizedCurrentPath = normalizePath(currentPath);
    const normalizedItemPath = normalizePath(itemPath, false);

    if (!normalizedItemPath) {
        return false;
    }

    return (
        normalizedCurrentPath === normalizedItemPath ||
        normalizedCurrentPath.startsWith(`${normalizedItemPath}/`)
    );
};

const isMenuItemActive = (item, currentPath) => {
    if (isPathActive(currentPath, item.path)) {
        return true;
    }

    return item.childItems?.nodes?.some((childItem) =>
        isPathActive(currentPath, childItem.path)
    );
};

const NavbarComponent = ({ data, variant, isBannerOpen, hideButton }) => {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileItemId, setOpenMobileItemId] = useState(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        document.addEventListener("scroll", handleScroll, { passive: true });
        return () => document.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((isOpen) => {
            if (isOpen) {
                setOpenMobileItemId(null);
            }

            return !isOpen;
        });
    };

    const toggleMobileMenuItem = (itemId) => {
        setOpenMobileItemId((openItemId) =>
            openItemId === itemId ? null : itemId
        );
    };

    const { navbarLogo: logo, button } = data.wp.mdaThemeOptions.themeSettings;

    const menuRaw = data.allWpMenu?.edges?.find((edge) =>
        edge.node.locations.includes("MAIN_MENU")
    );
    const menuItems = menuRaw ? menuRaw.node.menuItems.nodes : [];

    const topLevelItems = menuItems.filter((item) => item.parentId === null);

    return (
        <navbar className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
            <Container className="navbar__container">
                <Link to="/" className="navbar__logo-link">
                    {logo?.localFile?.childSvg?.content?.data ? (
                        <div
                            className="navbar__logo"
                            dangerouslySetInnerHTML={{
                                __html: logo.localFile.childSvg.content.data,
                            }}
                        />
                    ) : (
                        <img
                            src="/images/logo.svg"
                            alt="Wyden Logo"
                            className="navbar__logo"
                        />
                    )}
                </Link>

                <div
                    className={`navbar__menu ${
                        isMobileMenuOpen ? "navbar__menu--open" : ""
                    }`}
                >
                    <nav className="navbar__nav">
                        <ul className="navbar__nav-list">
                            <li className="navbar__nav-item navbar__nav-item--mobile-home">
                                <Link to="/" className="navbar__nav-link">
                                    Home
                                </Link>
                            </li>
                            {topLevelItems.map((item, index) => {
                                const hasChildren =
                                    item.childItems?.nodes &&
                                    item.childItems.nodes.length > 0;
                                const isOpen = openMobileItemId === item.id;
                                const isActive = isMenuItemActive(
                                    item,
                                    location.pathname
                                );
                                return (
                                    <li
                                        key={item.id}
                                        className={`navbar__nav-item ${
                                            hasChildren
                                                ? "navbar__nav-item--has-dropdown"
                                                : ""
                                        } ${
                                            isOpen
                                                ? "navbar__nav-item--open"
                                                : ""
                                        } ${
                                            isActive
                                                ? "navbar__nav-item--active"
                                                : ""
                                        }`}
                                    >
                                        {hasChildren ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className={`navbar__nav-link navbar__nav-link--dropdown ${
                                                        isActive
                                                            ? "navbar__nav-link--active"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        toggleMobileMenuItem(
                                                            item.id
                                                        )
                                                    }
                                                    aria-expanded={isOpen}
                                                >
                                                    {item.label}
                                                    <span
                                                        className="navbar__nav-chevron"
                                                        aria-hidden="true"
                                                    ></span>
                                                </button>
                                                <div className="navbar__dropdown">
                                                    <ul className="navbar__dropdown-list">
                                                        {item.childItems.nodes.map(
                                                            (subItem) => {
                                                                const isSubItemActive =
                                                                    isPathActive(
                                                                        location.pathname,
                                                                        subItem.path
                                                                    );

                                                                return (
                                                                    <li
                                                                        key={
                                                                            subItem.id
                                                                        }
                                                                        className="navbar__dropdown-item"
                                                                    >
                                                                        <Link
                                                                            to={
                                                                                subItem.path
                                                                            }
                                                                            className={`navbar__dropdown-link ${
                                                                                isSubItemActive
                                                                                    ? "navbar__dropdown-link--active"
                                                                                    : ""
                                                                            }`}
                                                                        >
                                                                            <span className="navbar__dropdown-arrow"></span>
                                                                            {
                                                                                subItem.label
                                                                            }
                                                                        </Link>
                                                                    </li>
                                                                );
                                                            }
                                                        )}
                                                    </ul>
                                                </div>
                                            </>
                                        ) : (
                                            <Link
                                                to={item.path}
                                                className={`navbar__nav-link ${
                                                    isActive
                                                        ? "navbar__nav-link--active"
                                                        : ""
                                                }`}
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    <div className="navbar__cta-wrapper">
                        {!hideButton && button && (
                            <Link
                                to={button.link.url || "/request-demo"}
                                target={button.link.target}
                                className="navbar__cta button button--primary"
                            >
                                {button.link.title || "Request a Demo"}
                            </Link>
                        )}
                    </div>
                </div>

                <button
                    className={`navbar__burger ${
                        isMobileMenuOpen ? "navbar__burger--active" : ""
                    }`}
                    onClick={toggleMobileMenu}
                    aria-label="Toggle menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </Container>
        </navbar>
    );
};

function Navbar(props) {
    return (
        <StaticQuery
            query={graphql`
                query {
                    wp {
                        mdaThemeOptions {
                            themeSettings {
                                navbarLogo {
                                    id
                                    altText
                                    localFile {
                                        childSvg {
                                            content {
                                                data
                                            }
                                        }
                                    }
                                }
                                button {
                                    link {
                                        url
                                        title
                                        target
                                    }
                                }
                            }
                        }
                    }
                    allWpMenu {
                        edges {
                            node {
                                id
                                locations
                                menuItems {
                                    nodes {
                                        id
                                        parentId
                                        label
                                        path
                                        childItems {
                                            nodes {
                                                id
                                                path
                                                label
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `}
            render={(data) => <NavbarComponent data={data} {...props} />}
        />
    );
}

export default Navbar;
